import React from "react";
import { Flex, theme, Tooltip } from "antd";
import type { GlobalToken } from "antd";
import { useTranslate } from "@refinedev/core";
import { QuestionIcon } from "@phosphor-icons/react";
import styled from "styled-components";
import { Button, radius } from "@libertify/ui";

interface SavePlaybookBarProps {
  isSaving: boolean;
  onSave: () => void;
}

/**
 * The customize view's primary action while the playbook itself is being
 * edited, in place of `CreateNowBar` — editing a playbook is not a route to
 * creating an experience.
 *
 * Saving is explicit: leaving with Back discards, which is what makes the
 * button the only thing that writes.
 */
export const SavePlaybookBar: React.FC<SavePlaybookBarProps> = ({
  isSaving,
  onSave,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  return (
    <Bar $token={token} align="center" gap={18}>
      {/* Says what a save does NOT touch: experiences already created keep
          the settings they were built with. */}
      <Tooltip title={t("createExperience.customize.savePlaybook.info")}>
        <InfoButton
          $token={token}
          type="button"
          aria-label={t("createExperience.customize.savePlaybook.infoLabel")}
        >
          <QuestionIcon size={14} weight="bold" />
        </InfoButton>
      </Tooltip>
      <Button variant="primary" size="l" loading={isSaving} onClick={onSave}>
        {t("createExperience.customize.savePlaybook.save")}
      </Button>
    </Bar>
  );
};

// ─── Styled components ───

interface TokenProps {
  $token: GlobalToken;
}

const Bar = styled(Flex)<TokenProps>`
  position: fixed;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  padding: 12px 16px;
  border-radius: ${radius.pill}px;
  background: ${({ $token }) => $token.colorBgElevated};
  border: 1px solid ${({ $token }) => $token.colorBorderSecondary};
  box-shadow: ${({ $token }) => $token.boxShadow};

  @media (width <= 700px) {
    left: 20px;
    right: 20px;
    transform: none;
    justify-content: space-between;
  }
`;

// A plain button, not `styled(Flex)`: `styled()` fixes Flex's generic at
// `FlexProps<AnyObject>`, so `component="button"` compiles but the element's
// own attributes (`type`) no longer typecheck.
const InfoButton = styled.button<TokenProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 1px solid ${({ $token }) => $token.colorBorder};
  border-radius: ${radius.circle};
  background: ${({ $token }) => $token.colorBgElevated};
  cursor: help;
  color: ${({ $token }) => $token.colorTextTertiary};

  &:hover {
    border-color: ${({ $token }) => $token.colorPrimary};
    color: ${({ $token }) => $token.colorPrimary};
  }
`;
